import { useState, useEffect, useRef } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import ActionButton from 'components/ActionButton';
import SmsConsentDisclosure from 'components/SmsConsentDisclosure/SmsConsentDisclosure';
import StatusMessage from 'components/StatusMessage';
import {
  useSendVerificationCodeMutation,
  useVerifyPhoneMutation
} from 'store/services/auth/authApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import { trackEvent, getUserProperties } from 'utils/analytics';
import { getPhoneVerificationErrorMessage } from 'utils/phoneVerificationErrors';
import '../components/LoginForm/forms/JoinWaitlistTab/JoinWaitlistTab.css';

type Props = {
  onNext: () => void;
};

const MobileVerificationStep = ({ onNext }: Props) => {
  const [phoneValue, setPhoneValue] = useState<string | undefined>('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [canResend, setCanResend] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);
  const [verificationSid, setVerificationSid] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);

  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [sendCode, { isLoading: isSending, error: sendError, reset: resetSendError }] = useSendVerificationCodeMutation();
  const [verifyPhone, { isLoading: isVerifying, error: verifyError, reset: resetVerifyError }] = useVerifyPhoneMutation();
  const { data: userData } = useGetMeQuery();

  // Handle resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const handleSendCode = async () => {
    if (!phoneValue) return;

    // Parse E.164 format phone number (e.g., "+14155551234")
    const matches = phoneValue.match(/^(\+\d{1,4})(\d+)$/);
    if (!matches) {
      console.error('Invalid phone number format');
      return;
    }

    const countryCode = matches[1]; // e.g., "+1"
    const digitsOnly = matches[2];  // e.g., "4155551234"

    try {
      const result = await sendCode({
        phoneNumber: digitsOnly,
        countryCode: countryCode
      }).unwrap();

      setVerificationSid(result.verificationSid);
      setShowCodeInput(true);
      setCanResend(false);
      setResendTimer(60); // 60 second cooldown
    } catch (err) {
      console.error('Failed to send verification code:', err);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;

    // A failed Resend renders its own error below the button. Drop any stale
    // verify error first so only the most recent error is on screen.
    resetVerifyError();

    // Clear the verification code inputs
    setVerificationCode(['', '', '', '', '', '']);

    // Reset focus to first input
    codeInputRefs.current[0]?.focus();

    // Send new code
    await handleSendCode();
  };

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(0, 1);

    // Typing a code means the user has moved on from a failed Resend.
    if (sendError) resetSendError();

    const newCode = [...verificationCode];
    newCode[index] = digit;
    setVerificationCode(newCode);

    // Auto-focus next input
    if (digit && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (sendError) resetSendError();
    const newCode = [...verificationCode];

    for (let i = 0; i < pastedText.length && i < 6; i++) {
      newCode[i] = pastedText[i];
    }

    setVerificationCode(newCode);

    // Focus the next empty input or the last one
    const nextEmptyIndex = newCode.findIndex(digit => !digit);
    if (nextEmptyIndex !== -1) {
      codeInputRefs.current[nextEmptyIndex]?.focus();
    } else {
      codeInputRefs.current[5]?.focus();
    }
  };

  const handleVerifyCode = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6 || !phoneValue) return;

    // Parse E.164 format to extract digits only
    const matches = phoneValue.match(/^(\+\d{1,4})(\d+)$/);
    if (!matches) {
      console.error('Invalid phone number format');
      return;
    }
    const digitsOnly = matches[2];

    try {
      await verifyPhone({
        phoneNumber: digitsOnly,
        code,
        verificationSid
      }).unwrap();

      // Track mobile verification in GA4
      trackEvent('mobile_verified', getUserProperties({
        id: userData?.id,
        createdAt: userData?.createdAt,
        country: userData?.country,
      }));

      // Successfully verified, move to next step
      onNext();
    } catch (err) {
      console.error('Failed to verify code:', err);
    }
  };

  const isPhoneValid = () => {
    // PhoneInput returns E.164 format (e.g., "+14155551234")
    // A valid phone number should have at least country code + 7 digits
    return phoneValue && phoneValue.length >= 8; // +1 + at least 7 digits
  };

  const isCodeComplete = () => {
    return verificationCode.every(digit => digit !== '');
  };

  return (
    <div className="login-form">
      <div className="login-form-content join-waitlist">
        <div className="onboarding-progress-dots">
          <div className="dot" />
          <div className="dot" />
          <div className="dot active" />
          <div className="dot" />
        </div>

        <h1 className="join-waitlist-title">Set up mobile 2FA</h1>
        <p className="join-waitlist-description">
          This keeps the community safe for all members.
        </p>

        <div className="join-waitlist-input">
          {!showCodeInput ? (
            <>
              <div className="phone-input-wrapper">
                <PhoneInput
                  international
                  defaultCountry="US"
                  value={phoneValue}
                  onChange={setPhoneValue}
                  className="react-phone-input"
                  placeholder="Enter phone number"
                />
              </div>

              <SmsConsentDisclosure />

              <ActionButton
                label="Continue"
                onPress={handleSendCode}
                isLoading={isSending}
                isDisabled={!isPhoneValid()}
              />

              {sendError && (
                <StatusMessage
                  type="error"
                  message={getPhoneVerificationErrorMessage(sendError, 'send')}
                />
              )}
            </>
          ) : (
            <>
              <div className="verification-code-section">
                <p className="verification-code-label">Enter the verification code</p>
                <p className="verification-code-sublabel">
                  We sent a 6-digit code to your mobile device.
                </p>

                <div className="verification-code-inputs">
                  {verificationCode.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        codeInputRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      className="code-input"
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      maxLength={1}
                      autoFocus={index === 0}
                    />
                  ))}
                </div>

                {resendTimer > 0 && (
                  <p className="resend-timer">
                    Resend code in {resendTimer} seconds
                  </p>
                )}

                {canResend && (
                  <button
                    className="resend-button"
                    onClick={handleResendCode}
                    disabled={isSending}
                  >
                    Resend code
                  </button>
                )}

                {/* Resend uses the send endpoint, so its failures (rate limit,
                    unsupported country, temporarily unavailable) surface here.
                    Without this a failed Resend renders nothing. */}
                {sendError ? (
                  <StatusMessage
                    type="error"
                    message={getPhoneVerificationErrorMessage(sendError, 'send')}
                  />
                ) : null}
              </div>

              <ActionButton
                label="Continue"
                onPress={handleVerifyCode}
                isLoading={isVerifying}
                isDisabled={!isCodeComplete()}
              />

              {verifyError && (
                <StatusMessage
                  type="error"
                  message={getPhoneVerificationErrorMessage(verifyError, 'verify')}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileVerificationStep;